import { IonIcon } from '@ionic/react'
import { msg } from '@lingui/core/macro'
import { useLingui } from '@lingui/react'
import { navigateOutline } from 'ionicons/icons'
import type { DateTime } from 'luxon'
import type { ReactNode, Ref } from 'react'
import { formatNameWithSecondary } from '../../helpers/formatNameWithSecondary'

// Order detail rows. Every field renders under its own condition, as a sibling
// of its neighbour (v1 parity): a missing lot name must not hide the pour type,
// and a missing site must not hide the onsite time (CRE-138).

type DetailColumnProps = {
  label: string
  value: ReactNode
  wide?: boolean
  children?: ReactNode
}

const DetailColumn = ({
  label,
  value,
  wide = false,
  children,
}: DetailColumnProps) => (
  <div
    className={
      wide ? 'order-detail__col order-detail__col--wide' : 'order-detail__col'
    }
  >
    <div className='order-detail__label'>{label}</div>
    <div className='order-detail__value'>{value}</div>
    {children}
  </div>
)

type SiteAddress = {
  street?: string | null
  city?: string | null
  state?: string | null
  lat?: number | null
  lng?: number | null
}

type Site = {
  name?: string | null
  name2?: string | null
  address?: SiteAddress | null
}

const formatCityState = (address: {
  city?: string | null
  state?: string | null
}): string => [address.city, address.state].filter(Boolean).join(', ')

type SiteRowProps = {
  site?: Site | null
  onsiteTime?: DateTime | null
  navRef: Ref<HTMLButtonElement>
}

export const SiteRow = ({ site, onsiteTime, navRef }: SiteRowProps) => {
  const { _ } = useLingui()
  const hasSiteRow = Boolean(site || onsiteTime)
  if (!hasSiteRow) return null

  const address = site?.address
  const canNavigate = address?.lat != null && address?.lng != null

  return (
    <div className='order-detail__row'>
      {site && (
        <DetailColumn
          wide
          label={_(msg`Site`)}
          value={formatNameWithSecondary(site.name, site.name2)}
        >
          {address && (
            <div className='order-detail__value order-detail__value--muted'>
              {address.street && (
                <>
                  {address.street}
                  <br />
                </>
              )}
              {formatCityState(address)}
            </div>
          )}
          {canNavigate && (
            <button
              type='button'
              ref={navRef}
              className='order-detail__nav-link'
            >
              <IonIcon icon={navigateOutline} />
              {_(msg`Navigate to Site`)}
            </button>
          )}
        </DetailColumn>
      )}
      {onsiteTime && (
        <DetailColumn
          label={_(msg`Onsite Time`)}
          value={onsiteTime.toLocaleString({
            hour: 'numeric',
            minute: '2-digit',
          })}
        />
      )}
    </div>
  )
}

type Pour = {
  name?: string | null
  type?: { name?: string | null } | null
}

export const PourDetailsRow = ({ pour }: { pour?: Pour | null }) => {
  const { _ } = useLingui()
  const hasPourDetails = Boolean(pour?.name || pour?.type?.name)
  if (!hasPourDetails) return null

  return (
    <div className='order-detail__row'>
      {pour?.name && (
        <DetailColumn wide label={_(msg`Lot / Location`)} value={pour.name} />
      )}
      {pour?.type?.name && (
        <DetailColumn label={_(msg`Pour Type`)} value={pour.type.name} />
      )}
    </div>
  )
}

type PourScheduleRowProps = {
  pourTime?: DateTime | null
  yardage?: string | number | null
  yardageLabel: string
}

export const PourScheduleRow = ({
  pourTime,
  yardage,
  yardageLabel,
}: PourScheduleRowProps) => {
  const { _ } = useLingui()
  const hasPourSchedule = Boolean(pourTime) || yardage != null
  if (!hasPourSchedule) return null

  return (
    <div className='order-detail__row'>
      {pourTime && (
        <DetailColumn
          wide
          label={_(msg`Pour Time`)}
          value={pourTime.toLocaleString({
            hour: 'numeric',
            minute: '2-digit',
          })}
        />
      )}
      {yardage != null && <DetailColumn label={yardageLabel} value={yardage} />}
    </div>
  )
}
